import React, { useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { ChatMessage as MessageComponent } from './ChatMessage'
import { MessageVersionsModal } from './MessageVersionsModal'
import { ChatComposer } from './ChatComposer'
import { InternalNotes } from './InternalNotes'
import { CloseModal } from './CloseModal'
import { WhisperBanner } from './WhisperBanner'
import { WhisperComposer } from './WhisperComposer'
import { ChatControlsMenu } from './ChatControlsMenu'
import { TransferModal } from './TransferModal'
import { InviteWhisperModal } from './InviteWhisperModal'
import { MissedChatBanner } from './MissedChatBanner'
import { ConversationInfoPanel } from './ConversationInfoPanel'
import { WidgetAccountSidebar } from './WidgetAccountSidebar'
import { WritingAssistant } from './WritingAssistant'
import { ArticleSharePanel } from './ArticleSharePanel'
import { SessionCountdown } from './SessionCountdown'
import { TimedSessionModal } from './TimedSessionModal'
import { RatingWidget } from './RatingWidget'
import { QuickBookButton } from '../crm/BookingModal'
import { CobrowseViewer } from '../cobrowse/CobrowseViewer'
// NewMessagesDivider available for future use
// import { NewMessagesDivider } from './NewMessagesDivider'
import { useChatThread, ChatMessage } from '../../hooks/useChatThread'
import { useCobrowse } from '../../hooks/useCobrowse'
import { useCobrowseFeature } from '../../hooks/useCobrowseFeature'
import { useChatLayout } from '../../contexts/ChatLayoutContext'
import { useWhisperMode } from '../../hooks/useWhisperMode'
import { AuthContext } from '../../contexts/AuthContext'
import { Badge } from '../shared/Badge'
import { formatDate, formatTime, formatDateTime } from '../../utils/format'
import { apiPost, apiGet, getTicketByChat, type Ticket } from '../../utils/api'
import { safeInitial, safeString } from '../../utils/safeString'
import { topicalTags } from '../../utils/chatTags'
import { useNavigate, useLocation } from 'react-router-dom'

interface ChatThreadProps {
  chatId: string
}

export const ChatThread: React.FC<ChatThreadProps> = ({ chatId }) => {
  const {
    chat,
    messages,
    loading,
    error,
    typingIndicator,
    typingPreview,
    suggestedReply,
    sendMessage,
    suggestReply,
    closeChat,
    addNote,
    acceptSuggestion,
    discardSuggestion,
    startTyping,
    stopTyping,
    editMessage,
    getMessageVersions,
  } = useChatThread(chatId)

  // Message whose version timeline is open.
  const [versionsFor, setVersionsFor] = React.useState<string | null>(null)

  const auth = useContext(AuthContext)
  const currentAgent = auth?.currentAgent
  const canObserve = currentAgent && ['admin', 'supervisor'].includes(currentAgent.role)

  const {
    isObserving,
    observers,
    whisperMessages,
    loading: whisperLoading,
    joinWhisper,
    leaveWhisper,
    sendWhisper,
    inviteWhisper,
  } = useWhisperMode(chatId)
  const [inviteOpen, setInviteOpen] = useState(false)

  // Determine channel type — WhatsApp gets WA-style bubbles, live chat keeps default
  const isWhatsAppChannel = chat?.channel === 'whatsapp'

  // Co-browse (Phase 1) — manual, view-only, consented. Only for live widget
  // chats that are open, and only when the deployment has the feature enabled.
  const cobrowseFeatureOn = useCobrowseFeature()
  const { state: cobrowseState, start: startCobrowse, stop: stopCobrowse } = useCobrowse(chatId)
  const cobrowseActive = cobrowseState === 'active'
  const canCobrowse = cobrowseFeatureOn && !isWhatsAppChannel && chat?.status === 'open'
  // Live view moved to its own window: session continues, chat goes full-width.
  const [cobrowsePoppedOut, setCobrowsePoppedOut] = useState(false)
  useEffect(() => {
    if (cobrowseState !== 'active') setCobrowsePoppedOut(false)
  }, [cobrowseState])
  const showCobrowsePane = cobrowseActive && !cobrowsePoppedOut

  // One-click "offer a screen share" — sends a friendly, consent-asking message
  // so the agent can tee up co-browse without typing it out each time.
  const [offeringShare, setOfferingShare] = useState(false)
  const SCREENSHARE_INVITE =
    "I think I can help you resolve this much faster over a quick screen share. Would you be okay with that? You'll get a prompt to allow it — it starts view-only and you can stop at any time. If it helps, I may then ask your permission to click through the page with you."
  const offerScreenShare = async () => {
    if (offeringShare) return
    setOfferingShare(true)
    try {
      await sendMessage(SCREENSHARE_INVITE)
    } finally {
      setOfferingShare(false)
    }
  }

  // Tell the workspace to hide the Chats list while co-browsing, so the agent
  // gets the visitor's screen + the chat with nothing else competing for width.
  const { setCobrowseActive } = useChatLayout()
  useEffect(() => {
    setCobrowseActive(cobrowseActive)
    return () => setCobrowseActive(false)
  }, [cobrowseActive, setCobrowseActive])

  // New feature states
  const [showEvents, setShowEvents] = useState(true)
  const [showInfoPanel, setShowInfoPanel] = useState(false)
  const [showAccountSidebar, setShowAccountSidebar] = useState(true)
  const [transferOpen, setTransferOpen] = useState(false)
  const [timedSessionOpen, setTimedSessionOpen] = useState(false)
  const [countdown, setCountdown] = useState<number | null>(null)
  const [articlePanelOpen, setArticlePanelOpen] = useState(false)
  const [composerText, setComposerText] = useState('')
  const [chatRating, setChatRating] = useState<any>(null)

  const navigate = useNavigate()
  const location = useLocation()

  // Back to the chats list, KEEPING the tab you came from (?status=missed etc.)
  // and the right list (My Chats vs Chats).
  const handleBack = () => {
    const base = location.pathname.startsWith('/my-chats') ? '/my-chats' : '/queue'
    navigate(`${base}${location.search || ''}`)
  }

  // Associated ticket (missed / notice-hours / ticketed chats only). Null when
  // the chat has no ticket — a normal live chat — in which case no buttons show.
  const [ticket, setTicket] = useState<Ticket | null>(null)

  // Load chat rating
  useEffect(() => {
    if (chatId && chat?.status === 'closed') {
      apiGet(`/chats/${chatId}/rating`).then((r: any) => setChatRating(r.rating)).catch(() => {})
    }
  }, [chatId, chat?.status])

  // Look up the ticket tied to this chat (404 → no ticket → null).
  useEffect(() => {
    if (!chatId) {
      setTicket(null)
      return
    }
    let active = true
    getTicketByChat(chatId)
      .then((t) => { if (active) setTicket(t) })
      .catch(() => { if (active) setTicket(null) })
    return () => { active = false }
  }, [chatId])

  // Every agent who has ANSWERED in this chat (from the message history +
  // live messages), so the header shows profile icon + name for each — even
  // when multiple agents attend one chat. The currently-assigned agent already
  // has their own chip; exclude them here to avoid a duplicate.
  const participants = useMemo(() => {
    const seen = new Map<string, { key: string; name: string; avatar?: string }>()
    for (const m of messages) {
      if (m.sender !== 'agent') continue
      const name = (m.senderName || '').trim()
      if (!name || name === 'Agent') continue
      const key = m.senderId != null ? `id:${m.senderId}` : `name:${name.toLowerCase()}`
      const existing = seen.get(key)
      if (!existing) seen.set(key, { key, name, avatar: m.senderAvatar })
      else if (!existing.avatar && m.senderAvatar) existing.avatar = m.senderAvatar
    }
    // Drop the assigned agent (already shown in the brand-blue chip).
    const assignedId = chat?.assignedAgentId != null ? `id:${chat.assignedAgentId}` : null
    const assignedName = (chat?.assignedAgentName || '').trim().toLowerCase()
    return [...seen.values()].filter(
      (p) => p.key !== assignedId && p.name.toLowerCase() !== assignedName
    )
  }, [messages, chat?.assignedAgentId, chat?.assignedAgentName])

  // Resolve this chat's company to its CRM lead so the header can offer the
  // usual Book-a-call/meeting flow (QuickBookButton). No matching lead → no
  // button. Best-effort; never blocks the thread.
  const [crmDeal, setCrmDeal] = useState<{ id: string; name: string | null } | null>(null)
  useEffect(() => {
    setCrmDeal(null)
    if (!chatId) return
    let active = true
    apiGet<any>(`/chats/${chatId}/crm-deal`)
      .then((r) => { if (active && r?.deal_id) setCrmDeal({ id: String(r.deal_id), name: r.company_name || null }) })
      .catch(() => { /* no lead — button simply doesn't render */ })
    return () => { active = false }
  }, [chatId])

  // The company logo — used as the avatar on the visitor's messages (nicer than
  // a coloured initial). Best-effort; falls back to a neutral initial.
  const [companyLogoUrl, setCompanyLogoUrl] = useState<string | null>(null)
  useEffect(() => {
    setCompanyLogoUrl(null)
    if (!chatId) return
    let active = true
    apiGet<any>(`/chats/${chatId}/account`)
      .then((d) => { if (active) setCompanyLogoUrl(d?.account?.company_logo_url || null) })
      .catch(() => { /* no logo — fall back to the initial */ })
    return () => { active = false }
  }, [chatId])

  const handleGoToTicket = useCallback(() => {
    if (ticket) navigate(`/tickets/${ticket.id}`)
  }, [ticket, navigate])

  // Merge whisper messages into the main timeline
  const allMessages = useMemo(() => {
    const whisperMapped: ChatMessage[] = whisperMessages.map((wm) => ({
      id: wm.id,
      chatId: wm.chat_id,
      sender: 'whisper' as const,
      senderName: wm.sender_name,
      content: wm.body,
      attachments: wm.attachments || [],
      isInternal: false,
      createdAt: wm.created_at,
    }))
    let merged = [...messages, ...whisperMapped]
    merged.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
    // Deduplicate by ID
    const seen = new Set<string>()
    merged = merged.filter((m) => {
      if (seen.has(m.id)) return false
      seen.add(m.id)
      return true
    })
    // Filter system events if hidden
    if (!showEvents) {
      merged = merged.filter(m => m.sender !== 'system')
    }
    return merged
  }, [messages, whisperMessages, showEvents])

  const [closeModalOpen, setCloseModalOpen] = React.useState(false)
  const [closing, setClosing] = React.useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [allMessages])

  const handleCountdownExpired = useCallback(() => {
    setCountdown(null)
    closeChat('timed_close', 'Session ended by timer').catch(() => {})
  }, [closeChat])

  if (loading && !chat) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex flex-col items-center gap-3">
          <svg className="w-8 h-8 text-brand-500 animate-spin-slow" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <span className="text-sm text-txt-secondary">Loading conversation...</span>
        </div>
      </div>
    )
  }

  if (!chat) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-8">
        <svg className="w-16 h-16 text-txt-tertiary mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
        </svg>
        <h3 className="text-lg font-semibold text-txt-primary mb-1">Conversation not found</h3>
        <p className="text-sm text-txt-secondary">Select a conversation from chats to get started</p>
      </div>
    )
  }

  const handleCloseChat = async (resolutionTag: string, notes: string) => {
    setClosing(true)
    try {
      await closeChat(resolutionTag, notes)
      setCloseModalOpen(false)
    } catch (err) {
      console.error('Failed to close chat:', err)
    } finally {
      setClosing(false)
    }
  }

  const handlePrint = () => {
    window.print()
  }

  // Build a clean, self-contained transcript and open it in a print window so the
  // agent can "Save as PDF" from the browser dialog. Works for ACTIVE and closed
  // chats alike (no server dependency — the old /save-pdf endpoint was a stub that
  // silently succeeded, so nothing ever happened). Internal whispers are excluded.
  const handleSavePdf = () => {
    try {
      const esc = (s: unknown) =>
        String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      const whoFor = (m: typeof messages[number]) =>
        m.sender === 'customer' ? (chat?.customerName || 'Customer')
          : m.sender === 'agent' ? (m.senderName || 'Agent')
          : m.sender === 'bot' ? (m.senderName || 'AI')
          : 'System'
      const fmt = (s?: string) => (s ? new Date(s).toLocaleString('en-GB') : '')

      const body = messages
        .filter((m) => m.sender !== 'whisper' && !m.isInternal)
        .map((m) => {
          const bodyHtml = m.messageType === 'html'
            ? m.content
            : esc(m.content).replace(/\n/g, '<br>')
          const atts = (m.attachments || [])
            .map((a: any) => `<div class="att">Attachment: ${esc(a.name || a.url || 'file')}</div>`)
            .join('')
          return `<div class="msg ${esc(m.sender)}">
              <div class="meta"><span class="who">${esc(whoFor(m))}</span><span class="when">${esc(fmt(m.createdAt))}</span></div>
              <div class="bubble">${bodyHtml || '<em class="empty">(no text)</em>'}${atts}</div>
            </div>`
        })
        .join('')

      const heading = esc(chat?.customerName || 'Conversation')
      const sub = [chat?.channel ? `Channel: ${chat.channel}` : '', chat?.status ? `Status: ${chat.status}` : '', chat?.createdAt ? `Started: ${fmt(chat.createdAt)}` : '']
        .filter(Boolean).map(esc).join(' · ')

      const html = `<!doctype html><html><head><meta charset="utf-8"><title>Chat transcript — ${heading}</title>
        <style>
          *{box-sizing:border-box} body{font-family:-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;color:#111;margin:32px;font-size:13px;line-height:1.5}
          h1{font-size:18px;margin:0 0 2px} .sub{color:#666;font-size:12px;margin:0 0 20px}
          .msg{margin:0 0 14px;page-break-inside:avoid} .meta{font-size:11px;color:#666;margin-bottom:3px}
          .who{font-weight:700;color:#222} .when{margin-left:8px}
          .bubble{border:1px solid #e5e7eb;border-radius:8px;padding:8px 10px;background:#fff;white-space:normal;word-wrap:break-word}
          .msg.agent .bubble{background:#eef4ff;border-color:#d6e4ff} .msg.bot .bubble{background:#f3f0ff;border-color:#e4dcff}
          .msg.system .bubble{background:#f7f7f8;border-color:#ececec;color:#666;font-style:italic}
          .att{font-size:11px;color:#2563eb;margin-top:4px} .empty{color:#999}
          @media print{body{margin:16px}}
        </style></head><body>
        <h1>Chat transcript — ${heading}</h1>
        <p class="sub">${sub}</p>
        ${body || '<p>No messages.</p>'}
        <script>window.onload=function(){setTimeout(function(){window.print()},150)}</script>
        </body></html>`

      const w = window.open('', '_blank')
      if (!w) { window.print(); return }
      w.document.open()
      w.document.write(html)
      w.document.close()
    } catch {
      window.print()
    }
  }

  const handleEmailTranscript = async () => {
    try {
      const res: any = await apiPost(`/chats/${chatId}/email-transcript`, {})
      const to: string[] = res?.sent_to || []
      window.alert(
        to.length
          ? `Transcript emailed to:\n${to.join('\n')}`
          : 'Transcript could not be emailed. Check the email configuration.'
      )
    } catch (err) {
      console.error('Failed to email transcript:', err)
      window.alert('Failed to email the transcript. Please try again.')
    }
  }

  const handleBlockIp = async () => {
    try {
      await apiPost('/blocked-ips', { ip_address: chat.sourceIp || 'unknown', chat_id: chatId, reason: 'Blocked from chat controls' })
    } catch (err) {
      console.error('Failed to block IP:', err)
    }
  }

  const handleDeleteChat = async () => {
    if (!window.confirm('Are you sure you want to archive this chat? It can be restored later from the Archived tab.')) return
    try {
      await apiPost(`/chats/${chatId}/delete`, {})
      // Navigate back to queue after successful archive
      window.location.href = '/queue'
    } catch (err) {
      console.error('Failed to archive chat:', err)
      alert('Failed to archive chat. Please try again.')
    }
  }

  const handleStartTimer = (seconds: number) => {
    if (seconds === 0) {
      handleCloseChat('timed_close', 'Session ended immediately')
    } else {
      setCountdown(seconds)
    }
  }

  const groupedMessages = (allMessages || []).reduce(
    (acc, msg) => {
      const date = formatDate(msg.createdAt)
      if (!acc[date]) acc[date] = []
      acc[date].push(msg)
      return acc
    },
    {} as Record<string, ChatMessage[]>
  )

  return (
    <div className="flex h-full bg-surface">
      {/* Co-browse: the visitor's live screen fills the main area; the chat
          docks to the right so the agent keeps talking while watching. */}
      {showCobrowsePane && (
        <div className="flex-1 min-w-0 h-full border-r border-border">
          <CobrowseViewer
            chatId={chatId}
            visitorLabel={chat.customerName || chat.customerId}
            onEnd={stopCobrowse}
            onPopOut={() => setCobrowsePoppedOut(true)}
          />
        </div>
      )}
      <div className={showCobrowsePane ? 'flex flex-col h-full w-[420px] flex-shrink-0' : 'flex flex-col h-full flex-1 min-w-0'}>
      {/* Thread header — wraps instead of overlapping when the panel is narrow */}
      <div className="flex-shrink-0 flex flex-wrap items-center justify-between gap-y-2 px-5 py-3 border-b border-border bg-surface">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <button
            onClick={handleBack}
            title="Back to chats"
            aria-label="Back to chats"
            className="flex items-center justify-center w-8 h-8 rounded-lg text-txt-secondary hover:text-txt-primary hover:bg-surface-hover transition-colors shrink-0"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-600 dark:text-brand-400 text-sm font-semibold shrink-0">
            {safeInitial(chat.customerName || chat.customerId)}
          </div>
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-txt-primary truncate">
              {safeString(chat.customerName || chat.customerId, 'Unknown')}
            </h2>
            {chat.customerPhone && (
              <p className="text-xs text-txt-secondary truncate">
                +{chat.customerPhone.replace(/^\+/, '')}
              </p>
            )}
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <Badge variant="status" value={chat.status} />
              {chat.assignedAgentName ? (
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-brand-50 dark:bg-brand-900/30 border border-brand-200 dark:border-brand-800 text-[11px] font-medium text-brand-700 dark:text-brand-300" title={`Handled by ${chat.assignedAgentName}`}>
                  {chat.assignedAgentAvatar ? (
                    <img src={chat.assignedAgentAvatar} alt="" className="w-3.5 h-3.5 rounded-full object-cover" />
                  ) : (
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                  )}
                  {chat.assignedAgentName}
                </span>
              ) : chat.status !== 'closed' ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-[11px] font-medium text-amber-700 dark:text-amber-400">
                  Unassigned
                </span>
              ) : null}
              {topicalTags(chat.tags).slice(0, 2).map((tag: string) => (
                <Badge key={tag} variant="tag" value={tag} />
              ))}
              {/* Every OTHER agent who has answered in this chat (multi-agent
                  visibility): profile icon + name, so the team can see who
                  else is/was working it. The current handler already has the
                  brand-blue chip above; these render alongside it. */}
              {participants.map((p) => (
                <span
                  key={p.key}
                  className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-surface-secondary border border-border text-[11px] font-medium text-txt-secondary"
                  title={`${p.name} has answered in this chat`}
                >
                  {p.avatar ? (
                    <img src={p.avatar} alt="" className="w-3.5 h-3.5 rounded-full object-cover" />
                  ) : (
                    <span className="w-3.5 h-3.5 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center text-[8px] font-bold">
                      {safeInitial(p.name)}
                    </span>
                  )}
                  {p.name}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 shrink-0">
          {/* Book a call / meeting with this chat's CRM lead — the usual
              PRD-04 scheduler flow. Renders only when the chat's company
              resolves to a lead. */}
          {!showCobrowsePane && crmDeal && (
            <QuickBookButton
              dealId={crmDeal.id}
              dealName={crmDeal.name || chat.customerName || undefined}
            />
          )}

          {/* Ticket actions — only for chats with an associated ticket
              (missed / notice-hours / ticketed). No ticket → no button.
              Tickets are email threads now; there is no "reopen as chat" path. */}
          {!showCobrowsePane && ticket && (
            <button
              onClick={handleGoToTicket}
              className="px-3 py-1.5 rounded-lg bg-surface-secondary hover:bg-surface-hover text-txt-primary border border-border text-sm font-medium transition-colors"
            >
              Go to Ticket
            </button>
          )}

          {/* Observer count badge */}
          {!showCobrowsePane && observers.length > 0 && (
            <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
              <svg className="w-3 h-3 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              <span className="text-[10px] font-semibold text-amber-600 dark:text-amber-400">
                {observers.length}
              </span>
            </div>
          )}

          {/* Whisper join/invite buttons live in the composer toolbar now
              (next to AI Suggest), per the whisper rework. */}

          {/* "Offer screen share" now lives by the composer (with a label) — see
              the ChatComposer whisperSlot below. */}

          {/* Co-browse (Phase 1) — manual, view-only, consented screen share.
              Never auto-triggered; the agent starts it only when needed. */}
          {canCobrowse && cobrowseState === 'idle' && (
            <button
              onClick={startCobrowse}
              title="Ask the visitor to share this page (view-only)"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-brand-700 dark:text-brand-300 bg-brand-50 dark:bg-brand-900/30 border border-brand-200 dark:border-brand-800 hover:bg-brand-100 dark:hover:bg-brand-900/50 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Start co-browse
            </button>
          )}
          {cobrowseState === 'requesting' && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
              <span className="w-3.5 h-3.5 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
              Waiting for consent…
              <button onClick={stopCobrowse} title="Cancel request" className="ml-1 text-amber-600 hover:text-amber-800">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          )}
          {cobrowseActive && cobrowsePoppedOut && (
            <button
              onClick={() => setCobrowsePoppedOut(false)}
              title="Bring the live view back into this window"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-brand-700 dark:text-brand-300 bg-brand-50 dark:bg-brand-900/30 border border-brand-200 dark:border-brand-800 hover:bg-brand-100 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 9V5a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2h-4M3 15v4a2 2 0 002 2h8a2 2 0 002-2v-4" />
              </svg>
              Live view (in window)
            </button>
          )}
          {cobrowseActive && cobrowsePoppedOut && (
            <button
              onClick={stopCobrowse}
              title="End co-browse"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold text-error border border-error/30 hover:bg-error-light transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
              End co-browse
            </button>
          )}

          {/* Timed session button */}
          {!showCobrowsePane && chat.status === 'open' && (
            <button
              onClick={() => setTimedSessionOpen(true)}
              className="p-1.5 rounded-lg text-txt-secondary hover:text-txt-primary hover:bg-surface-hover border border-border transition-colors"
              title="Timed session ending"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
          )}

          {/* Chat controls menu */}
          {!showCobrowsePane && (
            <ChatControlsMenu
              chatId={chatId}
              chatStatus={chat.status}
              sourceIp={chat.sourceIp}
              onTransfer={() => setTransferOpen(true)}
              onBlock={handleBlockIp}
              onDelete={handleDeleteChat}
              onPrint={handlePrint}
              onSavePdf={handleSavePdf}
              onEmailTranscript={handleEmailTranscript}
              onToggleEvents={() => setShowEvents(!showEvents)}
              showEvents={showEvents}
              onToggleInfo={() => setShowInfoPanel(!showInfoPanel)}
              showInfo={showInfoPanel}
            />
          )}

          {!showCobrowsePane && chat.status === 'open' && (
            <button
              onClick={() => setCloseModalOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-error border border-error/30 hover:bg-error-light transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Close
            </button>
          )}
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="mx-4 mt-3 px-4 py-2.5 rounded-lg bg-error-light border border-error/20 text-error text-sm">
          {error}
        </div>
      )}

      {/* Missed chat banner */}
      {chat.missedAt && (
        <MissedChatBanner
          missedAt={chat.missedAt}
          missedReason={chat.missedReason}
          onAssign={() => setTransferOpen(true)}
        />
      )}

      {/* Session countdown */}
      {countdown !== null && (
        <SessionCountdown
          seconds={countdown}
          onExpired={handleCountdownExpired}
          onCancel={() => setCountdown(null)}
        />
      )}

      {/* Messages area — WhatsApp-style background for WA channels, default for live chat */}
      <div
        className="flex-1 min-h-0 overflow-y-auto px-5 py-4"
        style={isWhatsAppChannel ? { backgroundColor: '#efeae2' } : undefined}
      >
        {Object.entries(groupedMessages).map(([date, dateMessages]) => (
          <div key={date}>
            {/* Date divider */}
            {isWhatsAppChannel ? (
              <div className="flex items-center justify-center my-3">
                <span
                  className="px-3 py-1.5 rounded-lg text-xs font-medium shadow-sm"
                  style={{ backgroundColor: '#d1f4cc', color: '#1b4332' }}
                >
                  {date}
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-4 my-4">
                <div className="flex-1 h-px bg-border" />
                <span className="text-[10px] font-semibold text-txt-tertiary uppercase tracking-wider">
                  {date}
                </span>
                <div className="flex-1 h-px bg-border" />
              </div>
            )}

            {dateMessages.map((msg, idx) => (
              <MessageComponent
                key={msg.id}
                message={msg}
                prevMessage={idx > 0 ? dateMessages[idx - 1] : undefined}
                isWhatsApp={isWhatsAppChannel}
                // Forwarding into the Copilot is offered on live (widget) chats.
                // WhatsApp views omit the source → Copy only (per design).
                forwardSource={
                  isWhatsAppChannel
                    ? undefined
                    : {
                        kind: 'chat',
                        id: chatId,
                        label: chat.customerName || chat.customerId || 'this chat',
                        path: `/queue/${chatId}`,
                      }
                }
                // Edit your own sent messages; view the version timeline on edited ones.
                onEditMessage={isWhatsAppChannel ? undefined : editMessage}
                onViewVersions={isWhatsAppChannel ? undefined : (id) => setVersionsFor(id)}
                currentAgentId={currentAgent?.id ? String(currentAgent.id) : undefined}
                visitorName={chat.customerName || undefined}
                companyLogoUrl={companyLogoUrl || undefined}
              />
            ))}
          </div>
        ))}

        {/* Typing indicator */}
        {typingIndicator && (
          <div className="flex items-center gap-2 mb-3 animate-fade-in">
            <div className="w-7 h-7 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
              <span className="text-[11px] font-semibold text-gray-600 dark:text-gray-300">
                {safeInitial(typingIndicator, 'A')}
              </span>
            </div>
            <div className="px-3.5 py-2.5 rounded-2xl rounded-tl-sm bg-surface-tertiary">
              <div className="flex gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-txt-tertiary animate-pulse-soft" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-txt-tertiary animate-pulse-soft" style={{ animationDelay: '200ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-txt-tertiary animate-pulse-soft" style={{ animationDelay: '400ms' }} />
              </div>
            </div>
            <span className="text-[11px] text-txt-tertiary">{typingIndicator} is typing</span>
          </div>
        )}

        {/* Live typing preview — shows what customer is typing in real-time */}
        {typingPreview && (
          <div className="flex gap-2.5 mb-3 justify-start animate-fade-in">
            <div className="shrink-0 flex items-center justify-center w-7 h-7 rounded-full bg-gray-200 dark:bg-gray-700">
              <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </div>
            <div className="max-w-[75%] flex flex-col items-start">
              <div className="flex items-center gap-1.5 mb-0.5">
                <span className="text-[10px] font-semibold text-txt-tertiary uppercase tracking-wide">Customer is typing</span>
                <span className="flex gap-0.5">
                  <span className="w-1 h-1 rounded-full bg-gray-300 animate-pulse-soft" style={{ animationDelay: '0ms' }} />
                  <span className="w-1 h-1 rounded-full bg-gray-300 animate-pulse-soft" style={{ animationDelay: '200ms' }} />
                  <span className="w-1 h-1 rounded-full bg-gray-300 animate-pulse-soft" style={{ animationDelay: '400ms' }} />
                </span>
              </div>
              <div className="px-3.5 py-2.5 rounded-2xl rounded-tl-sm border border-dashed border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/30">
                <span className="text-sm text-gray-400 dark:text-gray-500 italic">{typingPreview}</span>
              </div>
            </div>
          </div>
        )}

        {/* Chat ended banner — shown inside the scroll area when chat is closed */}
        {chat.status === 'closed' && (
          <div className="my-6 animate-fade-in">
            {/* Divider line */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 h-px bg-red-200 dark:bg-red-800/40" />
              <span className="text-[10px] font-semibold text-red-400 dark:text-red-500 uppercase tracking-wider">
                Conversation Ended
              </span>
              <div className="flex-1 h-px bg-red-200 dark:bg-red-800/40" />
            </div>

            {/* Status card */}
            <div className="mx-auto max-w-md rounded-xl border border-red-100 dark:border-red-900/30 bg-red-50/60 dark:bg-red-950/20 px-5 py-4">
              <div className="flex items-center gap-3 mb-2.5">
                <div className="flex items-center justify-center w-9 h-9 rounded-full bg-red-100 dark:bg-red-900/40">
                  <svg className="w-4.5 h-4.5 text-red-500 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-red-700 dark:text-red-300">
                    Chat Closed
                  </p>
                  <p className="text-xs text-red-500/80 dark:text-red-400/70">
                    {chat.closedAt
                      ? formatDateTime(chat.closedAt)
                      : 'Time not recorded'}
                  </p>
                </div>
              </div>

              {/* Resolution tag / reason */}
              {chat.resolutionTag && (
                <div className="flex items-center gap-2 mt-2 pt-2.5 border-t border-red-100 dark:border-red-900/30">
                  <svg className="w-3.5 h-3.5 text-red-400 dark:text-red-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                  <span className="text-xs font-medium text-red-600 dark:text-red-400">
                    {chat.resolutionTag}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Bottom chrome — composer, suggested replies, notes, writing-assist,
          rating and info panels all stack below the message area. Several can
          be open at once (e.g. notes + composer + article share), so the whole
          stack is capped and scrolls internally. Without this cap the combined
          height could push the flex column past its `h-full` bounds and the
          parent `overflow-hidden` would clip the header (and the sidebar) at
          the top. Same discipline as the whisper-mode cutoff fix. */}
      <div className="flex-shrink-0 max-h-[40vh] overflow-y-auto">
      {/* Conversation info panel — only for WhatsApp chats; widget chats show it
          in the right-hand sidebar instead. */}
      {showInfoPanel && isWhatsAppChannel && (
        <div className="flex-shrink-0">
          <ConversationInfoPanel chatId={chatId} />
        </div>
      )}

      {/* Internal notes / rating / article — these are the handler's panels.
          Hidden while observing so the whisper view stays clean and the column
          never overflows/clips the whisper composer. */}
      {!isObserving && (
        <>
          {/* Internal notes */}
          <div className="flex-shrink-0">
            <InternalNotes notes={chat.notes || []} onAddNote={addNote} />
          </div>

          {/* Rating widget (for closed chats) */}
          {chat.status === 'closed' && (
            <div className="flex-shrink-0">
              <RatingWidget chatId={chatId} existingRating={chatRating} />
            </div>
          )}

          {/* Article share panel */}
          <div className="flex-shrink-0">
            <ArticleSharePanel
              chatId={chatId}
              isOpen={articlePanelOpen}
              onClose={() => setArticlePanelOpen(false)}
              onShared={scrollToBottom}
            />
          </div>
        </>
      )}

      {/* Composer — only for open chats, and NOT while observing (an observer
          whispers via the whisper composer below; showing the customer composer
          too would both be wrong UX and overflow/clip the column). */}
      {chat.status === 'open' && !isObserving && (
        <div className="flex-shrink-0">
          {/* Writing assistant toolbar */}
          <WritingAssistant
            text={composerText}
            onResult={(result) => setComposerText(result)}
            chatId={chatId}
          />
          <ChatComposer
            chatId={chatId}
            compact={showCobrowsePane}
            onSendMessage={sendMessage}
            onSuggestReply={suggestReply}
            suggestedReply={suggestedReply}
            onAcceptSuggestion={acceptSuggestion}
            onDiscardSuggestion={discardSuggestion}
            onTypingStart={startTyping}
            onTypingStop={stopTyping}
            whisperSlot={(() => {
              if (isObserving) return null
              const isHandler = !!(currentAgent && chat.assignedAgentId && String(currentAgent.id) === String(chat.assignedAgentId))

              // "Offer screen share" — right by the composer, with a label so it's
              // easy to find (separate from Start co-browse / Request control).
              const offerBtn = canCobrowse && cobrowseState === 'idle' ? (
                <button
                  key="offer-share"
                  onClick={offerScreenShare}
                  disabled={offeringShare}
                  title="Send the visitor a quick message offering a screen share"
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-semibold text-brand-700 dark:text-brand-300 bg-brand-50 dark:bg-brand-900/30 hover:bg-brand-100 dark:hover:bg-brand-900/50 disabled:opacity-50 transition-colors"
                >
                  {offeringShare ? (
                    <span className="w-3.5 h-3.5 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8 21h8m-4-4v4m-7-4h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v9a2 2 0 002 2z" />
                    </svg>
                  )}
                  Offer screen share
                </button>
              ) : null

              let whisperBtn: React.ReactNode = null
              if (isHandler) {
                whisperBtn = (
                  <button
                    key="invite-whisper"
                    onClick={() => setInviteOpen(true)}
                    title="Invite a colleague to whisper on this chat"
                    className="flex items-center gap-1 px-2 py-1.5 rounded-md text-xs font-medium text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/30 transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" /></svg>
                    Invite to whisper
                  </button>
                )
              } else if (canObserve) {
                whisperBtn = (
                  <button
                    key="whisper"
                    onClick={joinWhisper}
                    disabled={whisperLoading}
                    title="Whisper mode — observe + whisper to the agent"
                    className="flex items-center gap-1 px-2 py-1.5 rounded-md text-xs font-medium text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/30 disabled:opacity-40 transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                    Whisper
                  </button>
                )
              }

              if (!offerBtn && !whisperBtn) return null
              return (
                <>
                  {offerBtn}
                  {whisperBtn}
                </>
              )
            })()}
          />
        </div>
      )}

      {/* Disabled input bar for closed chats */}
      {chat.status === 'closed' && !isObserving && (
        <div className="flex-shrink-0 border-t border-border bg-surface-secondary dark:bg-surface-secondary">
          <div className="flex items-center justify-center gap-2 px-4 py-3.5">
            <svg className="w-4 h-4 text-txt-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <span className="text-sm text-txt-tertiary font-medium">
              This conversation has ended
            </span>
            {chat.resolutionTag && (
              <>
                <span className="text-txt-tertiary mx-0.5">&middot;</span>
                <span className="text-xs text-txt-tertiary bg-surface-tertiary dark:bg-surface-active px-2 py-0.5 rounded-full">
                  {chat.resolutionTag}
                </span>
              </>
            )}
          </div>
        </div>
      )}

      {/* Whisper mode banner + composer */}
      {isObserving && (
        <div className="flex-shrink-0">
          <WhisperBanner
            observers={observers}
            isObserving={isObserving}
            onExit={leaveWhisper}
          />
          <WhisperComposer onSendWhisper={sendWhisper} />
        </div>
      )}

      </div>{/* end bottom chrome */}

      </div>{/* end chat column */}

      {/* Account / conversation info sidebar — widget (live) chats only.
          Collapsible: hides into the right edge, reopen via a chevron tab.
          Hidden while co-browsing to give the live view room. */}
      {!isWhatsAppChannel && !showCobrowsePane && (
        showAccountSidebar ? (
          <div className="relative flex-shrink-0">
            <button
              onClick={() => setShowAccountSidebar(false)}
              title="Hide details"
              className="absolute -left-3 top-3 z-10 flex items-center justify-center w-6 h-6 rounded-md bg-surface border border-border text-txt-secondary hover:text-brand-600 hover:bg-surface-hover shadow-sm transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
            </button>
            <WidgetAccountSidebar chatId={chatId} chat={chat} />
          </div>
        ) : (
          <button
            onClick={() => setShowAccountSidebar(true)}
            title="Show details"
            aria-label="Show details"
            className="flex-shrink-0 self-start mt-3 mr-2 flex items-center justify-center w-6 h-6 rounded-md bg-surface border border-border text-txt-secondary hover:text-brand-600 hover:bg-surface-hover shadow-sm transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
          </button>
        )
      )}

      {/* Message version timeline (internal record) */}
      {versionsFor && (
        <MessageVersionsModal
          messageId={versionsFor}
          load={getMessageVersions}
          onClose={() => setVersionsFor(null)}
        />
      )}

      {/* Close modal */}
      <CloseModal
        isOpen={closeModalOpen}
        onClose={() => setCloseModalOpen(false)}
        onConfirm={handleCloseChat}
        loading={closing}
      />

      {/* Transfer modal */}
      <TransferModal
        isOpen={transferOpen}
        chatId={chatId}
        currentAgentId={currentAgent?.id}
        onClose={() => setTransferOpen(false)}
        onTransferred={scrollToBottom}
      />

      {/* Invite-to-whisper modal */}
      <InviteWhisperModal
        isOpen={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onInvite={inviteWhisper}
        excludeAgentId={chat.assignedAgentId}
      />

      {/* Timed session modal */}
      <TimedSessionModal
        isOpen={timedSessionOpen}
        onClose={() => setTimedSessionOpen(false)}
        onStartTimer={handleStartTimer}
      />
    </div>
  )
}
